/**
 * ContributedItemCardContent.tsx
 *
 * Renders the content for a contributed item card (Recipe or Ingredient).
 * Displays image, name, status, and created date.
 *
 * Props:
 * - item: Recipe | Ingredient - The contributed item to display.
 */

import React from "react";
import { CardContent, CardMedia, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Recipe, Ingredient } from "../../types";
import { getStaticAssetPath } from "../../utils/staticAssets";

/**
 * Type guard to check if item is a Recipe.
 */
const isRecipe = (item: Recipe | Ingredient): item is Recipe =>
  "status_name" in item && "data" in item;

/**
 * Get the name of the item.
 */
const getName = (item: Recipe | Ingredient) =>
  isRecipe(item) ? item.data.name : item.name;

/**
 * Get the status of the item.
 */
const getStatus = (item: Recipe | Ingredient) =>
  isRecipe(item) ? item.status_name : "Ingredient";

/**
 * Get the image path for the item.
 * For recipes, uses coverImage if available, otherwise falls back to a default image.
 */
const getImagePath = (item: Recipe | Ingredient) => {
  if (isRecipe(item)) {
    if (item.data.coverImage && item.data.coverImage.source) {
      return getStaticAssetPath(item.data.coverImage.source);
    }
  }
  // Fallback to a dummy image if no cover image is found
  return getStaticAssetPath("images/recipe_shape_4.png");
};

/**
 * Get the created date of the item as a formatted string.
 */
const getCreatedDate = (item: Recipe | Ingredient) =>
  isRecipe(item)
    ? new Date(item.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

/**
 * ContributedItemCardContent component.
 */
const ContributedItemCardContent: React.FC<{ item: Recipe | Ingredient }> = ({
  item,
}) => {
  const theme = useTheme();
  const imagePath = getImagePath(item);
  const createdDate = getCreatedDate(item);

  return (
    <>
      {/* Display image if available */}
      {imagePath && (
        <CardMedia
          component="img"
          height="120"
          image={imagePath}
          alt={getName(item)}
          sx={{
            bgcolor: theme.palette.grey[300],
            objectFit: "cover",
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
            border: "none",
          }}
        />
      )}
      <CardContent sx={{ p: 2 }}>
        {/* Created date */}
        <Typography variant="caption" color="text.secondary">
          {createdDate}
        </Typography>
        {/* Name */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            lineHeight: 1.2,
            mt: 0.5,
            mb: 0.5,
            color: theme.palette.materiom.seaweed,
          }}
        >
          {getName(item)}
        </Typography>
        {/* Status */}
        <Typography
          variant="body2"
          sx={{ color: theme.palette.materiom.charcoal }}
        >
          {getStatus(item)}
        </Typography>
      </CardContent>
    </>
  );
};

export default ContributedItemCardContent;
